import { auth } from "@/lib/firebase";
import { collection, getDocs, limit, orderBy, query, startAt, endAt } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "@/components/ui/use-toast";
import { createPortal } from "react-dom";
import { scryImageUrl } from "@/lib/scryClient"; // CDN image helper

import CollectionPickerModal from "@/features/trades/CollectionPickerModal";
import ConfirmTradeModal from "@/features/trades/ConfirmTradeModal";
import OtherCollectionPickerModal from "@/features/trades/OtherCollectionPickerModal";
import SendProposalModal from "@/features/trades/SendProposalModal";
import TradeInboxModal from "@/features/trades/TradeInboxModal";
import TradePane from "@/features/trades/TradePane";
import TradesVariantPickerModal, { VariantPick } from "@/features/trades/TradesVariantPickerModal";
import { commitTradeToMyCollection } from "@/features/trades/tradeCommit";
import { computeTotal, formatCurrency, mergeTradeItems } from "@/features/trades/tradeHelpers";
import type { TradeDoc } from "@/features/trades/tradeProposalTypes";
import type { TradeItem } from "@/features/trades/tradeTypes";

const DRAFT_KEY = "trades-draft-v1";

export default function Trades() {
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged?.((u) => setUid(u?.uid ?? null));
    return () => unsub && unsub();
  }, []);

  const [sent, setSent] = useState<TradeItem[]>([]);
  const [received, setReceived] = useState<TradeItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [otherPickerOpen, setOtherPickerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commitBusy, setCommitBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);

  // Variant picker state
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantCtx, setVariantCtx] = useState<null | { side: "sent" | "received"; index: number; item: TradeItem }>(null);

  // Inline "find user" search for OtherCollectionPicker
  type UserLite = { uid: string; username: string; displayName?: string | null };
  const [inlineQUser, setInlineQUser] = useState("");
  const [inlineUsers, setInlineUsers] = useState<UserLite[]>([]);
  const [inlineBusy, setInlineBusy] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [selectedOtherUser, setSelectedOtherUser] = useState<UserLite | null>(null);
  // debounce timer ref
  const inlineTimer = useRef<number | null>(null);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.sent)) setSent(parsed.sent);
      if (Array.isArray(parsed?.received)) setReceived(parsed.received);
    } catch {}
  }, []);

  // Persist draft on change
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ sent, received }));
    } catch {}
  }, [sent, received]);

  const sentTotal = useMemo(() => computeTotal(sent), [sent]);
  const receivedTotal = useMemo(() => computeTotal(received), [received]);
  const diff = useMemo(() => receivedTotal - sentTotal, [receivedTotal, sentTotal]);
  const diffColor = diff === 0 ? "text-gray-300" : diff > 0 ? "text-emerald-400" : "text-amber-400";

  async function commitNow() {
    if (!uid) throw new Error("Not signed in.");
    setCommitBusy(true);
    try {
      const summary = await commitTradeToMyCollection({ uid, sent, received });
      setSent([]);
      setReceived([]);
      localStorage.removeItem(DRAFT_KEY);
      toast({
        variant: "success",
        title: "Trade committed",
        description: `${summary.decrementedDocs} decremented, ${summary.deletedDocs} deleted, ${summary.addedDocs} added`,
      });
    } finally {
      setCommitBusy(false);
    }
  }

  function loadProposalIntoBuilder(doc: TradeDoc) {
    if (!uid) return;
    const iAmProposer = doc.proposerUid === uid;
    const mySent = (iAmProposer ? doc.sentByProposer : doc.sentByRecipient) as any[];
    const myRecv = (iAmProposer ? doc.sentByRecipient : doc.sentByProposer) as any[];
    const now = Date.now();
    const toItem = (l: any, i: number): TradeItem => ({
    key: `${l.docId || l.scryfall_id || l.name}-${now}-${i}`,
    origin: l.origin,
    ownerUid: l.ownerUid ?? null,
    docId: l.docId ?? null,
    scryfall_id: l.scryfall_id ?? null,
    oracle_id: l.oracle_id ?? null, // <-- add this
    name: l.name,
    set: l.set ?? null,
    number: l.number ?? null,
    image: l.image ?? null,
    foil: Boolean(l.foil),
    qty: Math.max(1, Math.floor(l.qty || 1)),
    unitPrice: typeof l.unitPrice === "number" ? l.unitPrice : null,
    meta: {},
  });
    setSent(mySent.map(toItem));
    setReceived(myRecv.map(toItem));
    setInboxOpen(false);
  }

  function openVariantPicker(side: "sent" | "received", item: TradeItem, index: number) {
    setVariantCtx({ side, index, item });
    setVariantOpen(true);
  }

  function applyVariant(pick: VariantPick) {
    if (!variantCtx) return;
    const updater = (arr: TradeItem[]) =>
      arr.map((it, i) =>
        i === variantCtx.index
          ? {
              ...it,
              scryfall_id: pick.id,
              set: pick.set ?? null,
              number: pick.collector_number ?? null,
              image: (() => {
                // Always prefer Scryfall CDN by id; fallback to pick.image then prev
                if (pick.id) return scryImageUrl(pick.id, "normal");
                if (pick.image) return pick.image;
                return it.image ?? null;
              })(),
              foil: (() => {
                if (pick.selectedFinish === "foil") return true;
                if (pick.selectedFinish === "nonfoil") return false;
                if (pick.nonfoilAvailable) return false;
                if (pick.foilAvailable) return true;
                return it.foil;
              })(),
              unitPrice: (() => {
                const wantFoil =
                  pick.selectedFinish === "foil"
                    ? true
                    : pick.selectedFinish === "nonfoil"
                    ? false
                    : pick.nonfoilAvailable
                    ? false
                    : pick.foilAvailable
                    ? true
                    : it.foil;
                if (wantFoil && pick.prices?.usd_foil != null) return Number(pick.prices.usd_foil);
                if (!wantFoil && pick.prices?.usd != null) return Number(pick.prices.usd);
                return it.unitPrice ?? null;
              })(),
              meta: { ...(it.meta || {}), set_name: pick.set_name ?? undefined },
            }
          : it,
      );
    if (variantCtx.side === "sent") setSent(updater);
    else setReceived(updater);
    setVariantOpen(false);
    setVariantCtx(null);
  }

  // Deletion helpers + event bridge (supports slide/click delete from pane)
  function removeAt(side: "sent" | "received", index: number) {
    if (side === "sent") setSent((arr) => arr.filter((_, i) => i !== index));
    else setReceived((arr) => arr.filter((_, i) => i !== index));
  }
  function removeByKey(side: "sent" | "received", key: string) {
    if (side === "sent") setSent((arr) => arr.filter((it) => it.key !== key));
    else setReceived((arr) => arr.filter((it) => it.key !== key));
  }
  useEffect(() => {
    const handler = (ev: Event) => {
      const e = ev as CustomEvent<{ side?: "sent" | "received"; index?: number; key?: string }>;
      const side = e.detail?.side;
      if (side !== "sent" && side !== "received") return;
      if (typeof e.detail?.index === "number") removeAt(side, e.detail.index);
      else if (typeof e.detail?.key === "string") removeByKey(side, e.detail.key);
    };
    window.addEventListener("trade-remove-item", handler as EventListener);
    window.addEventListener("trade:remove-item", handler as EventListener);
    window.addEventListener("tradepane:remove", handler as EventListener);
    return () => {
      window.removeEventListener("trade-remove-item", handler as EventListener);
      window.removeEventListener("trade:remove-item", handler as EventListener);
      window.removeEventListener("tradepane:remove", handler as EventListener);
    };
  }, []);
  
  // Listen for quicksearch / V2Modal additions and insert into the correct side
  useEffect(() => {
    function onAdd(ev: Event) {
      const e = ev as CustomEvent<{ item?: any; target?: { side?: "sent" | "received"; index?: number } }>;
      const it = e?.detail?.item;
      if (!it) return;
      const explicit = e?.detail?.target;
      const marker = explicit ?? (window as any).__trade_quicksearch_target ?? { side: "sent" };
      try { delete (window as any).__trade_quicksearch_target; } catch {}

      const normalized: TradeItem = {
        key: it.key || `qs-${it.scryfall_id || Math.random().toString(36).slice(2, 8)}-${Date.now()}`,
        origin: it.origin || "quicksearch",
        ownerUid: it.ownerUid ?? null,
        docId: it.docId ?? null,
        scryfall_id: it.scryfall_id ?? null,
        name: it.name ?? it.scryfall_id ?? "Card",
        set: it.set ?? null,
        number: it.number ?? null,
        image: it.image ?? null,
        foil: Boolean(it.foil),
        qty: Math.max(1, Number(it.qty) || 1),
        unitPrice: typeof it.unitPrice === "number" ? it.unitPrice : null,
        meta: it.meta || {},
      };

      if (marker?.side === "received") {
        setReceived((prev) => {
          const next = [...prev];
          if (typeof marker.index === "number" && marker.index >= 0 && marker.index <= next.length) {
            next.splice(marker.index, 0, normalized);
          } else next.push(normalized);
          return next;
        });
      } else {
        setSent((prev) => {
          const next = [...prev];
          if (typeof marker.index === "number" && marker.index >= 0 && marker.index <= next.length) {
            next.splice(marker.index, 0, normalized);
          } else next.push(normalized);
          return next;
        });
      }
      // no toast on add
    }
    window.addEventListener("trade-add-item", onAdd as EventListener);
    return () => window.removeEventListener("trade-add-item", onAdd as EventListener);
  }, [setSent, setReceived]);

  // Inline user search helper (prefix search using usernames mapping then users)
  async function findUsersByPrefix(prefix: string): Promise<UserLite[]> {
    const p = prefix.trim().toLowerCase();
    if (!p) return [];
    try {
      // try usernames mapping first
      const usernamesRef = collection(db, "usernames");
      const snap = await getDocs(query(usernamesRef, orderBy("username_lower"), startAt(p), endAt(p + "\uf8ff"), limit(10)));
      const result: UserLite[] = [];
      snap.forEach((d) => {
        const v = d.data() as any;
        if (v?.uid) result.push({ uid: v.uid, username: v?.username || v?.username_lower || d.id, displayName: v?.displayName ?? null });
      });
      if (result.length > 0) return result;
    } catch { /* fallthrough to users collection */ }

    try {
      const usersRef = collection(db, "users");
      const snap = await getDocs(query(usersRef, orderBy("username_lower"), startAt(p), endAt(p + "\uf8ff"), limit(10)));
      const result: UserLite[] = [];
      snap.forEach((d) => {
        const v = d.data() as any;
        result.push({ uid: d.id, username: v?.username || v?.username_lower || v?.displayName || d.id, displayName: v?.displayName ?? null });
      });
      return result;
    } catch {
      return [];
    }
  }

  // Watch inlineQUser and run debounced search
  useEffect(() => {
    if (inlineTimer.current) window.clearTimeout(inlineTimer.current);
    if (!inlineQUser.trim()) {
      setInlineUsers([]);
      setInlineBusy(false);
      setInlineError(null);
      return;
    }
    setInlineBusy(true);
    setInlineError(null);
    inlineTimer.current = window.setTimeout(async () => {
      try {
        const found = await findUsersByPrefix(inlineQUser);
        setInlineUsers(found);
        if (found.length === 0) setInlineError("No users found.");
      } catch (e: any) {
        setInlineError(e?.message || "Search failed.");
      } finally {
        setInlineBusy(false);
      }
    }, 300);
    return () => {
      if (inlineTimer.current) window.clearTimeout(inlineTimer.current);
    };
  }, [inlineQUser]);

  // Measure bottom nav + footer, and render footer via a fixed portal above the nav
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  // Host element for the footer portal (only created on Trades page)
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null);
  // header height to dock top (so page sits under header)
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  // Sensible fallback if nav cannot be detected
  function defaultNavHeight(): number {
    try {
      return window.matchMedia && window.matchMedia("(min-width: 1024px)").matches ? 80 : 64;
    } catch {
      return 64;
    }
  }

  useEffect(() => {
    // Try to locate the bottom nav in the app shell
    const selectors = [
      "[data-bottom-nav]",
      "[data-app-bottom-nav]",
      'nav[aria-label="Bottom navigation"]',
      'nav[aria-label="App bottom navigation"]',
      "#bottom-nav",
      ".bottom-nav",
      ".app-bottom-nav",
      "footer.bottom-nav",
    ];

    const getCssVarHeight = (): number => {
      try {
        const root = document.documentElement;
        const styles = getComputedStyle(root);
        const vars = ["--app-bottom-nav-height", "--bottom-nav-height", "--app-bottom-nav"];
        for (const v of vars) {
          const raw = styles.getPropertyValue(v)?.trim();
          if (!raw) continue;
          const px = raw.endsWith("px") ? parseFloat(raw) : Number(raw);
          if (Number.isFinite(px) && px > 0) return px;
        }
      } catch {}
      return 0;
    };

    const findNav = () => {
      for (const s of selectors) {
        const el = document.querySelector(s) as HTMLElement | null;
        if (el) return el;
      }
      return null;
    };

    let navEl = findNav();
    const updateFromEl = () => {
      const cssVar = getCssVarHeight();
      const measured = navEl ? navEl.offsetHeight || 0 : 0;
      const finalH = Math.max(cssVar, measured, measured === 0 ? defaultNavHeight() : 0);
      setBottomNavHeight(finalH);
    };

    // Initial set
    updateFromEl();

    // Observe changes
    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;

    if (!navEl) {
      // If not found, watch DOM for a late-mounted nav
      mo = new MutationObserver(() => {
        const maybe = findNav();
        if (maybe && maybe !== navEl) {
          navEl = maybe;
          updateFromEl();
          if ("ResizeObserver" in window) {
            ro?.disconnect();
            ro = new ResizeObserver(updateFromEl);
            ro.observe(navEl);
          }
        } else {
          updateFromEl();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    }

    if (navEl && "ResizeObserver" in window) {
      ro = new ResizeObserver(updateFromEl);
      ro.observe(navEl);
    }

    const onResize = () => updateFromEl();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      ro?.disconnect();
      mo?.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Create the fixed portal host once (mount/unmount only)
  useEffect(() => {
    const host = document.createElement("div");
    host.setAttribute("id", "trades-footer-host");
    host.setAttribute("data-over-nav", "true");
    Object.assign(host.style, {
      position: "fixed",
      left: "0",
      right: "0",
      bottom: "0px", // set actual offset in next effect
      zIndex: "60",
      pointerEvents: "none", // child will re-enable for clicks
    } as CSSStyleDeclaration);
    document.body.appendChild(host);
    setFooterHost(host);

    return () => {
      try {
        document.body.removeChild(host);
      } catch {}
      setFooterHost(null);
    };
  }, []);

  // Keep the host glued above the bottom nav as its height changes
  useEffect(() => {
    if (!footerHost) return;
    footerHost.style.bottom = `${bottomNavHeight}px`;
  }, [bottomNavHeight, footerHost]);

  // Track footer height for content padding inside the scroll area
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const update = () => setFooterHeight(el.offsetHeight || 0);
    update();

    let ro: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, [footerRef]);

  // measure header (try common selectors)
  const selectors = ["header[data-app-header]", "header", ".app-header", "#app-header", "[data-app-top]"];
  const find = () => {
    for (const s of selectors) {
      const el = document.querySelector(s) as HTMLElement | null;
      if (el) return el;
    }
    return null;
  };
  let hdr = find();
  const updateHeader = () => {
    const h = hdr ? (hdr.offsetHeight || 0) : 0;
    setHeaderHeight(h);
  };
  useEffect(() => {
    if (!hdr) {
      const mo = new MutationObserver(() => {
        const maybe = find();
        if (maybe && maybe !== hdr) {
          hdr = maybe;
          updateHeader();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
      // try initial
      updateHeader();
      return () => mo.disconnect();
    } else {
      updateHeader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollPadBottom = Math.max(0, bottomNavHeight + footerHeight + 8); // 8px breathing room

  // Layout docked between header (top) and footer (below) so it sits "under header" and above trade buttons/footer
  const containerStyle: React.CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    top: headerHeight > 0 ? headerHeight : undefined,
    bottom: footerHost ? `${bottomNavHeight + footerHeight}px` : undefined,
    overflow: "hidden",
    zIndex: 40,
  };
  return (
    <div data-tour="trades-page" style={containerStyle}>
      {/* Scrollable content area (fills between top and footer) */}
      <div className="h-full overflow-auto p-2 sm:p-3" style={{ paddingBottom: 12 }}>
        <div className="rounded-md border border-gray-800 bg-black/30 p-2 sm:p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-base sm:text-lg md:text-xl font-semibold text-gray-100">Trades</div>
            <div className="flex items-center gap-2">
              {/* Inbox wrapper */}
              <div data-tour="trades-inbox">
                <button
                  className="rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  onClick={() => setInboxOpen(true)}
                  disabled={!uid}
                  title="Open trade inbox"
                  /* (button may still keep data-tour; safe redundancy) */
                  data-tour="trades-inbox"
                >
                  Inbox
                </button>
              </div>
              {/* Send Proposal wrapper */}
              <div data-tour="trades-send-proposal">
                <button
                  className="rounded border border-indigo-800 bg-indigo-600/80 font-medium text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  disabled={!uid || (sent.length === 0 && received.length === 0)}
                  onClick={() => setSendOpen(true)}
                  data-tour="trades-send-proposal"
                >
                  Send Proposal
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Two columns even on mobile; tighter gaps on small screens */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3">
          <div className="text-[11px] sm:text-[12px] md:text-[14px]">
            {/* Sent side wrapper */}
            <div data-tour="trades-sent">
              <TradePane
                side="sent"
                title="Cards I’m Sending"
                items={sent}
                setItems={setSent}
                onItemClick={(item, index) => openVariantPicker("sent", item, index)}
                actions={
                  uid ? (
                    <div className="flex w-full flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                      <button
                        className="rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5
                                   px-2 py-1 text-[11px] sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base
                                   w-full sm:w-auto truncate whitespace-nowrap overflow-hidden"
                        onClick={() => {
                          if (!uid) {
                            toast({ description: "Sign in to add from your collection." });
                            return;
                          }
                          setPickerOpen(true);
                        }}
                      >
                        <span className="truncate">Add from My Collection</span>
                      </button>
                    </div>
                  ) : null
                }
                hideSearch={true}
                data-tour="trades-sent"
              />
            </div>
          </div>

          <div className="text-[11px] sm:text-[12px] md:text-[14px]">
            {/* Received side wrapper */}
            <div data-tour="trades-received">
              {/* show selected "From @username" and reset control */}
              {selectedOtherUser && (
                <div className="mb-2 flex items-center gap-3">
                  <div className="text-xs text-gray-400">From</div>
                  <div className="font-medium text-gray-100">@{selectedOtherUser.username}</div>
                  <button
                    className="ml-2 text-xs rounded px-2 py-1 border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5"
                    onClick={() => {
                      // remove cards that came from the selected user (ownerUid matches)
                      const uidToClear = selectedOtherUser?.uid;
                      if (uidToClear) {
                        setReceived((prev) => prev.filter((it) => it.ownerUid !== uidToClear));
                      }
                      setSelectedOtherUser(null);
                      setInlineQUser("");
                      setInlineUsers([]);
                      toast({ description: "Cleared selected user and removed their cards" });
                    }}
                  >
                    Reset
                  </button>
                </div>
              )}

              <TradePane
                side="received"
                title="Cards I’m Receiving"
                items={received}
                setItems={setReceived}
                onItemClick={(item, index) => openVariantPicker("received", item, index)}
                actions={
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      <input
                        value={inlineQUser}
                        onChange={(e) => {
                          setInlineQUser(e.target.value);
                          // selecting a new query resets previously selected user
                          setSelectedOtherUser(null);
                        }}
                        placeholder="Find user by username…"
                        className="flex-1 rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-100"
                        aria-label="Find user"
                      />
                      <button
                        className="rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5 px-3 py-1"
                        onClick={() => {
                          if (!selectedOtherUser) {
                            toast({ description: "Select a user from the results to open their collection." });
                            return;
                          }
                          setOtherPickerOpen(true);
                        }}
                      >
                        <span className="truncate">Add from User Collection</span>
                      </button>
                    </div>
                    {/* Inline results row */}
                    <div className="overflow-x-auto">
                      <div className="flex gap-2 py-2 px-0">
                        {inlineBusy ? (
                          <div className="text-sm text-gray-400 px-2">Searching…</div>
                        ) : inlineUsers.length === 0 ? (
                          inlineQUser.trim() ? <div className="text-sm text-gray-400 px-2">{inlineError ?? "No users"}</div> : null
                        ) : (
                          inlineUsers.map((u) => {
                            const isSelected = selectedOtherUser?.uid === u.uid;
                            return (
                              <button
                                key={u.uid}
                                onClick={() => {
                                  setSelectedOtherUser(u);
                                  // clear results visually (keep q text)
                                  setInlineUsers([]);
                                }}
                                className={`flex items-center gap-2 px-3 py-1 rounded border ${isSelected ? "border-amber-500 bg-amber-600/10" : "border-gray-800 bg-black/20"}`}
                                title={`@${u.username}`}
                              >
                                <div className="text-sm text-gray-100">@{u.username}</div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                 }
                 hideSearch={true}
                 data-tour="trades-received"
               />
             </div>
           </div>
         </div>

        {/* Modals (inside scroll area is fine) */}
        <CollectionPickerModal
          open={pickerOpen}
          ownerUid={uid || ""}
          onClose={() => setPickerOpen(false)}
          onConfirm={(items) => {
            setPickerOpen(false);
            setSent((prev) => mergeTradeItems(prev, items));
            // no toast on adding from collection
          }}
        />
        <OtherCollectionPickerModal
          open={otherPickerOpen}
          initialUser={selectedOtherUser ?? undefined}
          onClose={() => setOtherPickerOpen(false)}
          onConfirm={(items) => {
            setOtherPickerOpen(false);
            setReceived((prev) => mergeTradeItems(prev, items));
            // no toast on adding from other user's collection
          }}
        />
        <ConfirmTradeModal
          open={confirmOpen}
          sent={sent}
          received={received}
          onClose={() => setConfirmOpen(false)}
          onCommit={commitNow}
        />
        <SendProposalModal
          open={Boolean(sendOpen && uid)}
          proposerUid={uid || ""}
          sentByProposer={sent}
          sentByRecipient={received}
          onClose={() => setSendOpen(false)}
          onCreated={() => {
            setSendOpen(false);
            toast({ variant: "success", title: "Proposal sent" });
          }}
        />
        <TradeInboxModal
          open={Boolean(inboxOpen && uid)}
          uid={uid || ""}
          onClose={() => setInboxOpen(false)}
          onLoadIntoBuilder={loadProposalIntoBuilder}
        />
        <TradesVariantPickerModal
          open={variantOpen && !!variantCtx}
          onClose={() => {
            setVariantOpen(false);
            setVariantCtx(null);
          }}
          name={variantCtx?.item.name || ""}
          scryfall_id={variantCtx?.item.scryfall_id || undefined}
          onSelect={applyVariant}
        />
      </div>

      {/* Footer rendered via portal so it sits above the bottom nav and only on Trades page */}
      {footerHost &&
        createPortal(
          <div
            ref={footerRef}
            data-tour="trades-footer"
            className="mx-auto w-full max-w-6xl border-t border-gray-800 bg-gray-950/95 px-2 sm:px-3 pt-2 pb-[calc(10px+env(safe-area-inset-bottom))] backdrop-blur supports-[backdrop-filter]:bg-gray-950/60 pointer-events-auto"
            role="region"
            aria-label="Trade actions"
          >
            <div className="w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 sm:gap-4 px-1">
                <div className="text-xs sm:text-sm text-gray-300">
                  Sending: <span className="font-medium text-gray-100">{formatCurrency(sentTotal)}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-300">
                  Receiving: <span className="font-medium text-gray-100">{formatCurrency(receivedTotal)}</span>
                </div>
                <div className={`text-xs sm:text-sm font-semibold ${diffColor}`}>
                  Diff: {formatCurrency(diff)} {diff === 0 ? "" : diff > 0 ? "(in your favor)" : "(you owe)"}
                </div>
              </div>

              {/* Buttons shrink on mobile (~1/2) and grow on desktop (~+1/3) */}
              <div className="grid w-full grid-cols-2 gap-2 sm:w-auto sm:grid-cols-4 sm:auto-cols-max sm:flex sm:gap-2">
                <button
                  className="w-full rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  onClick={() => {
                    setSent([]); setReceived([]); localStorage.removeItem(DRAFT_KEY);
                  }}
                >
                  Clear Both
                </button>
                <button
                  className="w-full rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  onClick={() => setSent([])}
                >
                  Clear Left
                </button>
                <button
                  className="w-full rounded border border-gray-700 bg-black/40 text-gray-200 hover:bg-white/5
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  onClick={() => {
                    // Clear received column and also clear any selected "other user"
                    setReceived([]);
                    setSelectedOtherUser(null);
                    setInlineQUser("");
                    setInlineUsers([]);
                    toast({ description: "Cleared Received column and reset selected user" });
                  }}
                 >
                   Clear Right
                 </button>
                <button
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60
                             px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4 lg:py-2.5 lg:text-base"
                  disabled={!uid || (sent.length === 0 && received.length === 0) || commitBusy}
                  onClick={() => setConfirmOpen(true)}
                >
                  {commitBusy ? "Working…" : "Confirm Trade"}
                </button>
              </div>
            </div>
          </div>,
          footerHost,
        )}

      <Toaster position="bottom-right" />
      {/* Listen for quicksearch add events (from V2Modal) */}
      <script suppressHydrationWarning={true} />
    </div>
  );
}
